import React, { Fragment, useEffect, useState } from 'react';
import { Popover as HUIPopover, Transition } from '@headlessui/react';
import clx from 'classnames';
import { createPortal } from 'react-dom';

type AppProps = {
  trigger: React.ReactNode | (({ open }: { open: boolean }) => React.ReactNode);
  children:
    | React.ReactNode
    | (({ close }: { close: () => void }) => React.ReactNode);
  panelClassName?: string;
  closeOnClick?: boolean;
  showOnHover?: boolean;
  usePortal?: boolean;
};

const Popover: React.FC<AppProps> = ({
  trigger,
  children,
  panelClassName,
  closeOnClick = true,
  showOnHover = false,
  usePortal = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [triggerRef, setTriggerRef] = useState<HTMLElement | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (usePortal && triggerRef) {
      const updatePosition = () => {
        if (triggerRef) {
          const rect = triggerRef.getBoundingClientRect();
          // Use getBoundingClientRect for fixed positioning (viewport coordinates)
          setPosition({
            top: rect.bottom + 4, // 4px gap below trigger
            left: rect.left, // Align with left edge of trigger
          });
        }
      };

      // Initial position update
      const timer = setTimeout(updatePosition, 0);

      const handleResize = () => updatePosition();
      const handleScroll = () => updatePosition();

      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, true);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [usePortal, triggerRef]);

  // Update position when hover state changes (for hover mode)
  useEffect(() => {
    if (usePortal && triggerRef && showOnHover && isHovered) {
      const updatePosition = () => {
        if (triggerRef) {
          const rect = triggerRef.getBoundingClientRect();
          setPosition({
            top: rect.bottom + 4,
            left: rect.left,
          });
        }
      };
      const timer = setTimeout(updatePosition, 0);
      return () => clearTimeout(timer);
    }
  }, [usePortal, triggerRef, showOnHover, isHovered]);

  return (
    <div className="relative">
      <HUIPopover className="relative group">
        {({ open, close }) => {
          const isVisible = showOnHover ? isHovered : open;
          const panelContent = (
            <div className="overflow-hidden rounded-md p-2 shadow-lg ring-1 ring-black ring-opacity-5">
              {typeof children === 'function'
                ? (
                    children as ({
                      close,
                    }: {
                      close: () => void;
                    }) => React.ReactNode
                  )({ close })
                : children}
            </div>
          );

          return (
            <>
              <HUIPopover.Button
                ref={setTriggerRef}
                className="ring-0 outline-none"
                onMouseEnter={() => showOnHover && setIsHovered(true)}
                onMouseLeave={() => showOnHover && setIsHovered(false)}
              >
                {typeof trigger === 'function' ? trigger({ open }) : trigger}
              </HUIPopover.Button>
              {usePortal ? (
                isVisible &&
                createPortal(
                  <Transition
                    as={Fragment}
                    show={isVisible}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                  >
                    <div
                      onClick={() => closeOnClick && close()}
                      onMouseEnter={() => showOnHover && setIsHovered(true)}
                      onMouseLeave={() => showOnHover && setIsHovered(false)}
                      className={clx(
                        'fixed z-[9999] rounded-lg transform bg-white',
                        panelClassName
                      )}
                      style={{
                        top: `${position.top}px`,
                        left: `${position.left}px`,
                      }}
                    >
                      {panelContent}
                    </div>
                  </Transition>,
                  document.body
                )
              ) : (
                <Transition
                  as={Fragment}
                  show={isVisible}
                  enter="transition ease-out duration-200"
                  enterFrom="opacity-0 translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-1"
                >
                  <HUIPopover.Panel
                    onClick={() => closeOnClick && close()}
                    onMouseEnter={() => showOnHover && setIsHovered(true)}
                    onMouseLeave={() => showOnHover && setIsHovered(false)}
                    className={clx(
                      'absolute right-0 z-[9999] mt-1 rounded-lg transform bg-white',
                      panelClassName
                    )}
                  >
                    {panelContent}
                  </HUIPopover.Panel>
                </Transition>
              )}
            </>
          );
        }}
      </HUIPopover>
    </div>
  );
};

export default Popover;

export const PortalPopover: React.FC<{
  trigger: React.ReactNode;
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  containerWidth?: string;
}> = ({
  trigger,
  children,
  open: controlledOpen,
  onOpenChange,
  containerWidth = '280px',
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const triggerRef = React.useRef<HTMLDivElement>(null);
  const [popoverRef, setPopoverRef] = useState<HTMLDivElement | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isPositioned, setIsPositioned] = useState(false);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setIsPositioned(false);
    }
    if (isControlled) {
      onOpenChange?.(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  };

  const updatePosition = React.useCallback(() => {
    if (triggerRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const popoverWidth = popoverRef?.offsetWidth || 280; // fallback width

      // Calculate position to place popover outside modal on the left
      // Position it so the right edge of popover aligns with left edge of trigger
      const leftPosition = triggerRect.left - popoverWidth - 40;

      // Ensure the popover doesn't go off-screen to the left
      const finalLeftPosition = Math.max(10, leftPosition);

      // Position popover so arrow points at the center of trigger (where + icon is)
      // Arrow is at 25% height of popover (top-1/4), so offset by quarter of the popover height
      const popoverHeight = popoverRef?.offsetHeight || 80;
      setPosition({
        top: triggerRect.top + triggerRect.height / 2 - popoverHeight * 0.25,
        left: finalLeftPosition,
      });
      setIsPositioned(true);
    }
  }, [popoverRef]);

  // Update position when isOpen changes or popoverRef is set
  useEffect(() => {
    if (isOpen) {
      // Multiple attempts to position correctly as the popover renders
      const timer1 = setTimeout(updatePosition, 0);
      const timer2 = setTimeout(updatePosition, 50);
      const timer3 = setTimeout(updatePosition, 150);

      const handleResize = () => updatePosition();
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleResize, true);
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleResize, true);
      };
    }
  }, [isOpen, updatePosition]);

  // Also update when popoverRef changes
  useEffect(() => {
    if (isOpen && popoverRef) {
      updatePosition();
    }
  }, [isOpen, popoverRef, updatePosition]);

  const popoverContent = (
    <>
      {/* Backdrop for both controlled and uncontrolled modes */}
      <div
        className="fixed inset-0"
        style={{ zIndex: 999998 }}
        onClick={() => handleOpenChange(false)}
      />
      <div
        ref={setPopoverRef}
        className="fixed bg-white rounded-lg shadow-xl border border-gray-200 p-2"
        style={{
          top: position.top,
          left: position.left,
          zIndex: 999999,
          width: containerWidth,
          opacity: isPositioned ? 1 : 0,
          transition: 'opacity 0.15s ease-in-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
        {/* Arrow border (behind) */}
        <div className="absolute right-0 top-1/4 transform translate-x-full -translate-y-1/2">
          <div className="w-0 h-0 border-t-[11px] border-t-transparent border-b-[11px] border-b-transparent border-l-[11px] border-l-gray-300"></div>
        </div>
        {/* Arrow fill (on top) */}
        <div
          className="absolute right-0 top-1/4 transform translate-x-full -translate-y-1/2"
          style={{ marginRight: '1px' }}
        >
          <div className="w-0 h-0 border-t-[10px] border-t-transparent border-b-[10px] border-b-transparent border-l-[10px] border-l-white"></div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <div ref={triggerRef} className="cursor-pointer">
        {trigger}
      </div>
      {isOpen && createPortal(popoverContent, document.body)}
    </>
  );
};
